import { useCallback, useRef, useState } from "react";
import { UploadCloud, FileText, X } from "lucide-react";

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function FileDrop({ files, onAdd, onRemove, accept = "application/pdf", multiple = true, hint = "PDF files up to a few hundred MB", renderMeta }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback((list) => {
    onAdd(Array.from(list));
  }, [onAdd]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        data-testid="upload-dropzone"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-2xl p-8 sm:p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200 ${
          drag ? "border-blue-500 bg-blue-50" : "border-blue-300 bg-blue-50/50 hover:border-blue-500 hover:bg-blue-50"
        }`}
      >
        <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20">
          <UploadCloud className="w-7 h-7 text-white" />
        </div>
        <p className="font-heading font-bold text-slate-900 text-lg">
          {drag ? "Drop your file to start" : "Upload or drag & drop"}
        </p>
        <p className="text-sm text-slate-500 mt-1">{hint}</p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          data-testid="file-input"
          onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {files.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="file-list">
          {files.map((f, i) => (
            <li key={f.id ?? i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3" data-testid={`file-row-${i}`}>
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 truncate">{f.name}</p>
                <p className="text-xs text-slate-500">
                  {fmtSize(f.size)}
                  {f.pageCount != null && ` · ${f.pageCount} page${f.pageCount === 1 ? "" : "s"}`}
                </p>
                {renderMeta && renderMeta(f, i)}
              </div>
              <button
                onClick={() => onRemove(i)}
                data-testid={`file-remove-${i}`}
                className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                aria-label="Remove file"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export { fmtSize };
